import { motion } from "framer-motion";
import { Minus, Plus, Trash2 } from "lucide-react";
import { useCart } from "../context/CartContext";
import { discountOf, formatINR } from "../lib/api";

const BADGE_STYLES = {
  BESTSELLER: "bg-ember text-obsidian",
  NEW: "bg-discount-green text-obsidian",
  PREMIUM: "bg-crimson text-starlight",
};

export default function ProductCard({ product }) {
  const { add, items, setQty } = useCart();
  const inCart = items.find((i) => i.product.id === product.id);
  const discount = discountOf(product);

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    e.currentTarget.style.setProperty("--mx", `${e.clientX - r.left}px`);
    e.currentTarget.style.setProperty("--my", `${e.clientY - r.top}px`);
  };

  return (
    <motion.article
      variants={{ hidden: { opacity: 0, y: 28 }, show: { opacity: 1, y: 0 } }}
      onMouseMove={onMove}
      data-testid={`product-card-${product.id}`}
      className="spotlight-card group relative rounded-2xl border border-starlight/10 bg-card p-3 transition-colors duration-300 hover:border-ember/50"
    >
      <div className="relative overflow-hidden rounded-xl bg-black aspect-square">
        <img
          src={product.image}
          alt={product.name}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
        <span className="absolute inset-0 bg-[radial-gradient(circle_at_50%_35%,transparent_55%,rgba(5,6,11,0.55)_100%)]" />
        {product.badge && (
          <span
            className={`absolute top-3 left-3 rounded-full px-2.5 py-1 font-mono text-[9px] font-bold tracking-[0.18em] ${BADGE_STYLES[product.badge] || "bg-ember text-obsidian"}`}
          >
            {product.badge}
          </span>
        )}
        <span className="absolute top-3 right-3 rounded-full bg-obsidian/80 backdrop-blur px-2.5 py-1 font-mono text-[10px] font-bold text-discount-green border border-discount-green/40">
          GREAT DEAL
        </span>
        <span className="absolute bottom-3 right-3 flex items-end gap-0.5" title={`Sound level ${product.sound} of 5`}>
          {[1, 2, 3, 4, 5].map((b) => (
            <span
              key={b}
              className={`w-1 rounded-sm ${b <= product.sound ? "bg-ember" : "bg-starlight/20"}`}
              style={{ height: `${4 + b * 2.5}px` }}
            />
          ))}
        </span>
      </div>

      <div className="p-3">
        <h3 className="font-semibold text-starlight leading-snug">{product.name}</h3>
        <p className="mt-0.5 text-xs text-muted-night">{product.tamil}</p>
        <p className="mt-2 font-mono text-[10px] tracking-[0.14em] uppercase text-muted-night/80">
          {product.content}
        </p>

        <div className="mt-3 flex items-baseline gap-2">
          <span className="font-mono text-xl font-bold text-ember">{formatINR(product.price)}</span>
          <span className="font-mono text-xs text-muted-night line-through">{formatINR(product.mrp)}</span>
        </div>

        {inCart ? (
          <div
            data-testid={`qty-stepper-${product.id}`}
            className="mt-3 flex items-center justify-between rounded-lg border border-ember/60 bg-ember/10 px-2 py-1.5"
          >
            <button
              data-testid={`qty-decrease-${product.id}`}
              onClick={() => setQty(product.id, inCart.qty - 1)}
              className="flex h-7 w-7 items-center justify-center rounded-md text-ember hover:bg-ember hover:text-obsidian transition-colors"
              aria-label={`Decrease ${product.name}`}
            >
              {inCart.qty === 1 ? <Trash2 className="h-3.5 w-3.5" /> : <Minus className="h-3.5 w-3.5" />}
            </button>
            <span className="font-mono text-sm font-bold text-ember">{inCart.qty} in box</span>
            <button
              data-testid={`qty-increase-${product.id}`}
              onClick={() => setQty(product.id, inCart.qty + 1)}
              className="flex h-7 w-7 items-center justify-center rounded-md text-ember hover:bg-ember hover:text-obsidian transition-colors"
              aria-label={`Increase ${product.name}`}
            >
              <Plus className="h-3.5 w-3.5" />
            </button>
          </div>
        ) : (
          <button
            data-testid={`add-to-cart-button-${product.id}`}
            onClick={() => add(product)}
            className="mt-3 flex w-full items-center justify-center gap-2 rounded-lg border border-ember/50 py-2.5 font-mono text-[11px] font-bold tracking-[0.2em] uppercase text-ember transition-all duration-300 hover:bg-ember hover:text-obsidian"
          >
            <Plus className="h-3.5 w-3.5" /> Add to cart
          </button>
        )}
      </div>
    </motion.article>
  );
}
